import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FiCheckCircle } from 'react-icons/fi';
import { TbRocket, TbTrendingUp, TbBuilding } from 'react-icons/tb';

import { Button, buttonVariants, buttonSizes } from '@components/Button';
import { StatusBadge, statusBadgeTones } from '@components/StatusBadge';
import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';

import styles from './PlanCard.module.scss';

const CURRENCY_SYMBOLS = { INR: '₹', USD: '$' };

const BILLING_PERIOD_LABEL = {
  monthly: '/user/month',
  quarterly: '/user/quarter',
  yearly: '/user/year',
};

const PLAN_ICON_CONFIG = {
  starter: {
    Icon: TbRocket,
    bg: 'var(--success-100)',
    color: 'var(--success-600)',
  },
  grow: {
    Icon: TbTrendingUp,
    bg: 'var(--purple-100)',
    color: 'var(--purple-600)',
  },
  enterprise: {
    Icon: TbBuilding,
    bg: 'var(--orange-100)',
    color: 'var(--orange-600)',
  },
};

export const PlanCardComponent = ({
  currentActivePlanId,
  currentInActivePlanId,
  showCycleEndWarning,
  subscriptionEndDate,
  cancelledAt,
  plan,
  billingCycle,
  currencyCode,
  isSelected,
  onSelect,
  onSubscribe,
  isLoading,
  onCancelPlan,
  onContactUs,
  onModifySeats,
}) => {
  const subscriptionData = useSelector(
    (state) => state.auth?.profile?.subscription
  );
  const isSeatsExhausted = useMemo(
    () =>
      subscriptionData?.occupiedSeats >= subscriptionData?.seats &&
      subscriptionData?.seatsExhausted,
    [subscriptionData]
  );

  const isEnterprise = plan.id === 'enterprise';
  const pricing = plan.pricing?.[currencyCode] || plan.pricing?.USD || {};
  const price = pricing[billingCycle] ?? pricing.monthly;

  const savings = useMemo(() => {
    const monthlyPrice = plan.pricing?.[currencyCode]?.monthly;
    if (!monthlyPrice || !price) return 0;
    const cycleFactor =
      billingCycle === 'quarterly' ? 3 : billingCycle === 'yearly' ? 12 : 1;
    const currentMonthlyEquivalent = price / cycleFactor;
    const savingsPercent =
      ((monthlyPrice - currentMonthlyEquivalent) / monthlyPrice) * 100;
    return savingsPercent > 0 ? Math.round(savingsPercent) : 0;
  }, [plan.pricing, currencyCode, price, billingCycle]);

  const isCurrentPlanPurchased =
    (typeof currentActivePlanId === 'string' &&
      currentActivePlanId === plan.razorpayPlanId[billingCycle]) ||
    (typeof currentInActivePlanId === 'string' &&
      currentInActivePlanId === plan.razorpayPlanId[billingCycle]);

  const showCancelButton =
    typeof currentActivePlanId === 'string' && isCurrentPlanPurchased;

  const subscribeButtonText = isCurrentPlanPurchased
    ? 'Current Plan'
    : typeof currentActivePlanId === 'string'
      ? 'Change Plan'
      : `Select ${plan.name}`;

  const iconConfig = PLAN_ICON_CONFIG[plan.id];
  const featuresHeading = plan.previousTierName
    ? `Everything in ${plan.previousTierName}, plus:`
    : `Everything in ${plan.name} includes:`;

  return (
    <div
      className={classNames(styles.card, {
        [styles.card_selected]: isSelected,
        [styles.card_popular]: plan.popular,
      })}
      style={{ '--plan-accent': iconConfig.color }}
      onClick={() => {
        if (isCurrentPlanPurchased) return;
        onSelect();
      }}
    >
      {plan.popular && (
        <div className={styles.popularBadgeWrapper}>
          <StatusBadge label="Most Popular" tone={statusBadgeTones.purple} />
        </div>
      )}

      <div className={styles.header}>
        <div
          className={styles.planIcon}
          style={{ background: iconConfig.bg, color: iconConfig.color }}
        >
          <iconConfig.Icon size={22} />
        </div>
        <div className={styles.planInfo}>
          <Text
            variant={textVariantsEnum.Text_lg}
            fontWeight={fontWeightEnum.Bold}
            color="gray-900"
          >
            {plan.name}
          </Text>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Normal}
            color="gray-500"
          >
            {plan.description}
          </Text>
        </div>
      </div>

      {!isEnterprise ? (
        <div className={styles.pricing}>
          <div className={styles.priceRow}>
            <span className={styles.currency}>
              {CURRENCY_SYMBOLS[currencyCode] || '$'}
            </span>
            <span className={styles.price}>{price}</span>
            <span className={styles.period}>
              {BILLING_PERIOD_LABEL[billingCycle] ||
                BILLING_PERIOD_LABEL.monthly}
            </span>
          </div>
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Normal}
            color="gray-500"
          >
            Minimum 2 users
          </Text>
          {savings > 0 && (
            <StatusBadge
              label={`Save ${savings}%`}
              tone={statusBadgeTones.success}
              className={styles.savingsBadge}
            />
          )}
        </div>
      ) : (
        <div className={styles.pricing}>
          <Text
            variant={textVariantsEnum.Text_xl}
            fontWeight={fontWeightEnum.Bold}
            color="gray-900"
          >
            Contact Sales Team
          </Text>
          <Text
            variant={textVariantsEnum.Text_xs}
            fontWeight={fontWeightEnum.Normal}
            color="gray-500"
          >
            Custom pricing
          </Text>
        </div>
      )}

      <div className={styles.actions}>
        {!isEnterprise && !isCurrentPlanPurchased && (
          <Button
            text={subscribeButtonText}
            variant={
              isSelected
                ? buttonVariants.primary
                : buttonVariants.primary_outline
            }
            size={buttonSizes.lg}
            style={
              isSelected
                ? { borderRadius: '10px' }
                : {
                    borderRadius: '10px',
                    color: iconConfig.color,
                    borderColor: iconConfig.color,
                  }
            }
            loading={isLoading && isSelected}
            disabled={isLoading}
            onClick={onSubscribe}
          />
        )}

        {isCurrentPlanPurchased && isSeatsExhausted && (
          <Button
            text="Modify Seats"
            variant={buttonVariants.outline}
            size={buttonSizes.lg}
            disabled={isLoading}
            onClick={onModifySeats}
          />
        )}

        {isCurrentPlanPurchased && (
          <Button
            text="Current Plan"
            variant={buttonVariants.secondary}
            size={buttonSizes.lg}
            disabled
          />
        )}

        {isEnterprise && (
          <Button
            text="Contact Sales"
            variant={
              isSelected
                ? buttonVariants.primary
                : buttonVariants.primary_outline
            }
            size={buttonSizes.lg}
            style={
              isSelected
                ? { borderRadius: '10px' }
                : {
                    borderRadius: '10px',
                    color: iconConfig.color,
                    borderColor: iconConfig.color,
                  }
            }
            disabled={isLoading}
            onClick={onContactUs}
          />
        )}

        {showCancelButton && (
          <Button
            text="Cancel Plan"
            variant={buttonVariants.ghost_danger}
            size={buttonSizes.lg}
            disabled={isLoading}
            onClick={onCancelPlan}
          />
        )}
      </div>

      <div className={styles.featuresBlock}>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-500"
        >
          {featuresHeading}
        </Text>
        <ul className={styles.featureList}>
          {plan.features.map((feature) => (
            <li key={feature} className={styles.featureItem}>
              <FiCheckCircle className={styles.featureIcon} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {isCurrentPlanPurchased && showCycleEndWarning && (
        <div className={styles.cycleEndNote}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
            color="primary-500"
          >
            Your subscription cycle ends on:
          </Text>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Semibold}
            color="primary-500"
          >
            {subscriptionEndDate}
          </Text>
        </div>
      )}
    </div>
  );
};

PlanCardComponent.displayName = 'PlanCard';
